import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { ILeadFormUIProps } from 'types/ILeadFormUIProps';
import { Input, Button, Textarea, Checkbox, ErrorMessage } from '@makemydeal/ui-bricks/dist/cox';
import { IShopper } from '@makemydeal/dr-platform-types';
import { BreakPoint, IconFactory } from '@makemydeal/dr-activities-common';
import {
    validateEmail,
    validateFirstName,
    validateLastName,
    validatePhone,
    validateRequiredField
} from '@makemydeal/dr-common-utils';
import { charactersRemaining } from '../utils/charactersRemaining';
import { SpinnerUI } from '@makemydeal/dr-activities-common';

const { useState, useEffect } = React;
const TRANSITION_MILISECONDS = 800;

enum ButtonState {
    DEFAULT = 'DEFAULT',
    SENDING = 'SENDING',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE'
}

interface IErrorMessage {
    hasError?: boolean;
}

const DisclaimerSection = styled.div<IErrorMessage>`
    display: flex;
    font-size: ${rem(12)};
    color: ${({ theme, hasError }) => {
        if (hasError) return theme.errorMessageTextColor;
        return theme.leadFormContainerDisclaimerColor;
    }};
    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(14)};
        margin: ${rem(8)};
    }
`;
const CCPASection = styled.div<IErrorMessage>`
    display: flex;
    font-size: ${rem(12)};
    margin-top: 1rem;
    color: ${({ theme, hasError }) => {
        if (hasError) return theme.errorMessageTextColor;
        return theme.leadFormContainerDisclaimerColor;
    }};
    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(14)};
        margin: ${rem(8)};
    }
`;

const LeadFormContainer = styled.form`
    padding: 16px;
    background-color: ${({ theme }) => theme.leadFormContainerBackgroundColor};
    height: 100%;
    font-family: ${({ theme }) => theme.primaryFontFamily || 'Open Sans'};
    border-radius: 8px;
`;

const LeadFormRow = styled.div`
    display: flex;
    flex-direction: column;
    @media (min-width: ${BreakPoint.SMALL}) {
        flex-direction: row;
    }
`;

const LeadFormTitle = styled.div`
    font-size: ${rem(24)};
    color: ${({ theme }) => theme.leadFormTitleColor};
    margin: ${rem(8)} 0;

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const LeadFormDescription = styled.div`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.leadFormDescriptionColor};
    margin: ${rem(8)} 0;
    margin-bottom: 2rem;

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
        margin-bottom: 2rem;
    }
`;

const LeadFormSubmitError = styled.div`
    margin: ${rem(8)} 0;
    border: 1px ${({ theme }) => theme.errorMessageTextColor} solid;
    padding: ${rem(8)} 0 ${rem(8)} ${rem(8)};

    & > div {
        margin: 0;
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const LeadFormSubLabel = styled.span`
    color: ${({ theme }) => theme.leadFormSubLabelColor};
    font-weight: normal;
`;

const LeadFormInputWrapper = styled.div`
    @media (min-width: ${BreakPoint.SMALL}) {
        width: 50%;
        margin: 0 ${rem(8)};
    }
`;

const LeadFormCommentWrapper = styled.div`
    margin-bottom: ${rem(16)};
    @media (min-width: ${BreakPoint.SMALL}) {
        margin: 0 ${rem(8)} ${rem(16)} ${rem(8)};
    }
`;

const MessageLengthCounter = styled.div`
    font-size: ${rem(14)};
    color: ${({ theme }) => theme.leadFormMessageLengthCounterColor};
    text-align: right;
`;

const LeadFormButtonWrapper = styled.div`
    box-sizing: border-box;
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    margin-top: ${rem(40)};
    padding-bottom: 2rem;

    @media (min-width: ${BreakPoint.SMALL}) {
        align-items: center;
    }

    .dr-sp-spinner {
        position: relative;
        top: 43px;
        right: 3px;
    }
`;

const LeadFormSkipLink = styled.button`
    align-self: center;
    margin: ${rem(16)} 0;
    font-size: ${rem(16)};
    font-weight: ${({ theme }) => theme.leadFormSkipLinkFontWeight};
    border-style: unset;
    cursor: pointer;

    :disabled {
        cursor: unset;
        color: ${({ theme }) => theme.primaryButtonDisabledBackgroundColor};
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        font-size: ${rem(18)};
    }
`;

const SubmitButton = styled(Button as any)`
    min-width: 226px;
    min-height: 50px;
    background-color: ${({ theme, status }) => {
        if (status === ButtonState.DEFAULT) return theme.primaryButtonColor;
        if (status === ButtonState.SENDING) return theme.primaryButtonDisabledBackgroundColor;
        if (status === ButtonState.FAILURE) return theme.primaryButtonFailureBackgroundColor;
        if (status === ButtonState.SUCCESS) return theme.primaryButtonSuccessBackgroundColor;
    }};
    :hover {
        background-color: ${({ theme, status }) => {
            if (status === ButtonState.DEFAULT) return theme.primaryButtonHoverColor;
            if (status === ButtonState.SENDING) return theme.primaryButtonDisabledBackgroundColor;
            if (status === ButtonState.FAILURE) return theme.primaryButtonFailureBackgroundColor;
            if (status === ButtonState.SUCCESS) return theme.primaryButtonSuccessBackgroundColor;
        }};
    }
`;

const WhiteIcon = styled(IconFactory)`
    svg {
        width: 27px;
        height: 20px;
        g {
            path {
                fill: ${({ theme }) => theme.leadFormWhiteIcon}};
            }
        }
    }
`;

const ConsentError = styled.div`
    margin: ${rem(8)} 0;
    padding: ${rem(8)} 0 ${rem(8)} ${rem(8)};

    & > div {
        margin: 0;
    }

    @media (min-width: ${BreakPoint.SMALL}) {
        margin: ${rem(8)};
    }
`;

const RemoveTrade = styled.a`
    text-decoration: none;
    margin-left: ${rem(3)};
    color: ${({ theme }) => theme.primaryLinkColor};

    ::hover {
        text-decoration: underline;
        color: ${({ theme }) => theme.primaryLinkHoverColor};
    }
`;

const timeout = () => new Promise((res) => setTimeout(res, TRANSITION_MILISECONDS));

export const LeadFormUI: React.FC<ILeadFormUIProps> = ({
    shopper,
    leadFormSubmitting,
    leadFormSubmittedSuccess,
    leadFormSubmittedFailure,
    leadFormReinitialized,
    isLeadFormOptional,
    shopperConsentText,
    isTCPAEnabled,
    isTCPACheckboxDisabled,
    CCPAConsentText,
    title,
    description,
    successAction,
    staticImages,
    externalErrorRetries,
    errorDescription,
    preSubmitAction,
    onSubmit,
    onLeadFormSubmittedSuccess,
    onFirstNameBlur,
    onLastNameBlur,
    onPhoneBlur,
    onEmailBlur,
    onMessageBlur,
    routeWithoutSubmitting,
    backToDashboard,
    onReinitializeForm,
    dispatchPreSubmitEvent,
    dismissPaymentTermsFallbackMessage,
    isPhoneNumberRequired,
    zipCodeLoading,
    leadFormCCPAShown,
    isPaymentTermsFallback,
    isTradePaymentFailed,
    dealerPhoneNumber,
    removeTradeInFromSummary
}) => {
    const [shopperInformation, setShopperInformation] = useState({
        firstName: shopper?.firstName || '',
        lastName: shopper?.lastName || '',
        zip: shopper?.zip || '',
        phone: shopper?.phone || '',
        pixallId: shopper?.pixallId || '',
        email: shopper?.email || '',
        contactPreferences: shopper?.contactPreferences || { email: false, sms: false, phone: false },
        message: shopper?.message || '',
        antennaId: shopper?.antennaId || '',
        userConsent: shopper?.userConsent || 'unchecked'
    } as IShopper);
    const [checked, setCheckboxChecked] = useState(false);
    const [submitButtonValue, setSubmitButtonValue] = useState(<>Continue</>);
    const [submitButtonBackground, setSubmitButtonBackground] = useState(ButtonState.DEFAULT);
    const [skipButtonEnabled, setSkipButtonEnabled] = useState(true);
    const [hasSubmitError, setHasSubmitError] = useState(false);
    const [leadFormSubmitError, setLeadFormError] = useState('An error has occurred.');
    const [isValidFirstName, setIsValidFirstName] = useState(true);
    const [isValidLastName, setIsValidLastName] = useState(true);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [isValidPhone, setIsValidPhone] = useState(true);
    const [isOnSubmitClicked, setIsOnSubmitClicked] = useState(false);

    const shouldShowDisclaimer = (shopper: IShopper) => {
        return shopper?.phone?.length > 0;
    };

    const setSubmitButtonToDefault = () => {
        setSubmitButtonBackground(ButtonState.DEFAULT);
        setSubmitButtonValue(<>Continue</>);
    };

    const handleSecondaryActionErrors = () => {
        if (externalErrorRetries > 0) {
            setSubmitButtonBackground(ButtonState.FAILURE);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="close" />);
            setLeadFormError(errorDescription);
            setHasSubmitError(true);
            timeout().then(() => {
                setSubmitButtonToDefault();
            });
        }
    };

    const handleLeadFormInitialize = (success, submitted, failure, reinitialized) => {
        if (!success && !submitted && !failure && !reinitialized) {
            setSubmitButtonValue(<>Continue</>);
            setSkipButtonEnabled(true);
            setHasSubmitError(false);
        }
    };

    const handleSubmitSuccess = (success: boolean, action: string) => {
        if (success) {
            setSubmitButtonBackground(ButtonState.SUCCESS);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="check" />);
            timeout().then(() => {
                onLeadFormSubmittedSuccess(action);
                setSubmitButtonToDefault();
            });
        } else {
            setSubmitButtonToDefault();
        }
    };
    const handleSubmitButtonState = (submitting: boolean) => {
        if (submitting) {
            setSubmitButtonBackground(ButtonState.SENDING);
            setSubmitButtonValue(<>Sending...</>);
        }
    };

    const handleSkipButtonState = (submitting: boolean) => {
        setSkipButtonEnabled(!submitting);
    };
    const handleReinitializeForm = () => {
        onReinitializeForm();
    };

    const handleSubmitError = (failed: boolean) => {
        if (failed) {
            setSubmitButtonBackground(ButtonState.FAILURE);
            setSubmitButtonValue(<WhiteIcon staticImages={staticImages} icon="close" />);
            setHasSubmitError(true);
            timeout().then(() => {
                handleReinitializeForm();
                setSubmitButtonToDefault();
            });
        } else {
            setSubmitButtonToDefault();
        }
    };

    const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCheckboxChecked(e.target.checked);
    };

    const normalizePhone = (value, previousValue) => {
        if (!value) {
            return value;
        }
        const onlyNums = value.replace(/[^\d]/g, '');
        if (!previousValue || value.length > previousValue.length) {
            // typing forward
            if (onlyNums.length === 3) {
                return `${onlyNums}-`;
            }
            if (onlyNums.length === 6) {
                return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}-`;
            }
        }
        if (onlyNums.length <= 3) {
            return onlyNums;
        }
        if (onlyNums.length <= 6) {
            return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3)}`;
        }
        return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
    };

    const handleChange = (e) => {
        const key = e.target.name;
        const caret = e.target.selectionStart;
        let value = e.target.value;

        if (key === 'phone') {
            const previousValue = e.target.defaultValue;
            window.requestAnimationFrame(() => {
                e.target.selectionStart = caret + 1;
                e.target.selectionEnd = caret + 1;
            });
            value = normalizePhone(value, previousValue);
        }
        const updatedShopperInformation = { ...shopperInformation, [key]: value };

        if (!shouldShowDisclaimer(updatedShopperInformation)) {
            updatedShopperInformation.userConsent = 'unchecked';
        }
        setShopperInformation(updatedShopperInformation);
    };

    const validateOptionalPhone = (value: string) => {
        if (isPhoneNumberRequired) {
            return validatePhone(value);
        }
        return !validateRequiredField(value) || validatePhone(value);
    };

    const updateValidationStates = (shopper: IShopper) => {
        setIsValidFirstName(validateFirstName(shopper.firstName));
        setIsValidLastName(validateLastName(shopper.lastName));
        setIsValidEmail(validateEmail(shopper.email));
        setIsOnSubmitClicked(true);
        setIsValidPhone(validateOptionalPhone(shopper.phone));
    };

    const isValid = (shopper: IShopper) => {
        const valid =
            !validateFirstName(shopper.firstName) ||
            !validateLastName(shopper.lastName) ||
            !validateEmail(shopper.email) ||
            !validateOptionalPhone(shopper.phone) ||
            (isTCPAEnabled && !isTCPACheckboxDisabled && !checked && shouldShowDisclaimer(shopper))
                ? false
                : true;

        if (!validateFirstName(shopper.firstName)) {
            document.getElementById('firstName__label')?.scrollIntoView();
        } else if (!validateLastName(shopper.lastName)) {
            document.getElementById('lastName__label')?.scrollIntoView();
        } else if (!validateEmail(shopper.email)) {
            document.getElementById('email__label')?.scrollIntoView();
        } else if (!validateOptionalPhone(shopper.phone)) {
            document.getElementById('phone__label')?.scrollIntoView();
        }

        updateValidationStates(shopper);
        return valid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (isPaymentTermsFallback) {
            dismissPaymentTermsFallbackMessage();
        }
        if (isValid(shopperInformation) && externalErrorRetries > 0) {
            setHasSubmitError(false);

            if (preSubmitAction) {
                dispatchPreSubmitEvent(preSubmitAction);
            } else {
                onLeadFormSubmittedSuccess(successAction);
            }
        } else if (isValid(shopperInformation)) {
            if (preSubmitAction) {
                dispatchPreSubmitEvent(preSubmitAction);
            } else {
                onSubmit(shopper);
            }
        } else {
            setHasSubmitError(false);
        }
    };

    const routeToDashboard = () => {
        backToDashboard();
    };

    const skipLeadForm = () => {
        routeWithoutSubmitting();
    };

    const renderSkipButton = () => {
        return isLeadFormOptional ? (
            <LeadFormSkipLink className="primary-link" type="button" disabled={!skipButtonEnabled} onClick={skipLeadForm}>
                No thanks, continue to next step
            </LeadFormSkipLink>
        ) : (
            <LeadFormSkipLink className="primary-link" type="button" disabled={!skipButtonEnabled} onClick={routeToDashboard}>
                Back To My Deal
            </LeadFormSkipLink>
        );
    };

    const renderDisclaimer = () => {
        const showDisclaimer = shouldShowDisclaimer(shopperInformation);
        if (!isTCPAEnabled || !showDisclaimer) return;
        const isTCPARequired = !isTCPACheckboxDisabled && !checked && isOnSubmitClicked;
        const consentError = isTCPARequired && showDisclaimer ? 'Your consent is required if phone number is provided.' : null;

        return (
            <div>
                <DisclaimerSection data-testid="disclaimer-section" hasError={isTCPARequired}>
                    {!isTCPACheckboxDisabled && (
                        <Checkbox
                            onChange={handleCheckbox}
                            checked={checked}
                            hasError={isTCPARequired}
                            data-testid="disclaimer-checkbox"
                        />
                    )}
                    <div>{shopperConsentText}</div>
                </DisclaimerSection>
                {consentError && (
                    <ConsentError data-testid="disclaimer-error">
                        <ErrorMessage>{consentError}</ErrorMessage>
                    </ConsentError>
                )}
            </div>
        );
    };

    const renderCCPADisclaimer = () => {
        if (leadFormCCPAShown) return <CCPASection data-testid="disclaimer-ccpa-section">{CCPAConsentText}</CCPASection>;
        return null;
    };

    const handleFirstNameBlur = () => {
        onFirstNameBlur(shopperInformation);
    };

    const handleLastNameBlur = () => {
        onLastNameBlur(shopperInformation);
    };

    const handleEmailBlur = () => {
        onEmailBlur(shopperInformation);
    };

    const handlePhoneBlur = () => {
        onPhoneBlur(shopperInformation);
    };

    const handleMessageBlur = () => {
        onMessageBlur(shopperInformation);
    };

    const maxMessageLength = 500;

    useEffect(() => {
        if (shopper) {
            shopper.userConsent = checked ? 'checked' : 'unchecked';
        }
        setShopperInformation({ ...shopper });
    }, [shopper, checked]);
    useEffect(() => {
        handleLeadFormInitialize(leadFormSubmittedSuccess, leadFormSubmitting, leadFormSubmittedFailure, leadFormReinitialized);
    }, [leadFormSubmittedSuccess, leadFormSubmitting, leadFormSubmittedFailure]);
    useEffect(() => {
        handleSubmitButtonState(leadFormSubmitting);
        handleSkipButtonState(leadFormSubmitting);
    }, [leadFormSubmitting]);
    useEffect(() => {
        handleSubmitError(leadFormSubmittedFailure);
    }, [leadFormSubmittedFailure]);
    useEffect(() => {
        handleSubmitSuccess(leadFormSubmittedSuccess, successAction);
    }, [leadFormSubmittedSuccess]);
    useEffect(() => {
        handleSecondaryActionErrors();
    }, [externalErrorRetries]);

    const remainingCharacters = charactersRemaining(shopperInformation.message, maxMessageLength);

    const tradeErrorMessage = `Oops. We couldn't calculate a monthly payment based on your trade-in value. Try removing your trade to receive a payment or contact the dealer at ${dealerPhoneNumber}.`;
    const TradeErrorMessage = ErrorMessage as any;

    return (
        <LeadFormContainer data-testid="dr-activities-lead-form">
            <LeadFormTitle className="common-header">{title}</LeadFormTitle>
            <LeadFormDescription>{description}</LeadFormDescription>
            {hasSubmitError && (
                <LeadFormSubmitError>
                    <ErrorMessage>{leadFormSubmitError}</ErrorMessage>
                </LeadFormSubmitError>
            )}
            {isTradePaymentFailed && (
                <LeadFormSubmitError data-testid="dr-lead-form-trade-payment-error">
                    <TradeErrorMessage>
                        {tradeErrorMessage}
                        <RemoveTrade onClick={removeTradeInFromSummary} data-testid="dr-lead-form-remove-trade">
                            Remove Trade-In
                        </RemoveTrade>
                    </TradeErrorMessage>
                </LeadFormSubmitError>
            )}
            <LeadFormRow>
                <LeadFormInputWrapper>
                    <Input
                        name="firstName"
                        value={shopperInformation.firstName}
                        label="First Name"
                        type="text"
                        placeholder="e.g. John"
                        onChangeFunction={handleChange}
                        onBlurFunction={handleFirstNameBlur}
                        validationFunction={validateFirstName}
                        hasError={!isValidFirstName}
                        errorMessage="Please enter a valid first name."
                        inputId="firstName"
                    />
                </LeadFormInputWrapper>
                <LeadFormInputWrapper>
                    <Input
                        name="lastName"
                        value={shopperInformation.lastName}
                        label="Last Name"
                        type="text"
                        placeholder="e.g. Smith"
                        onChangeFunction={handleChange}
                        onBlurFunction={handleLastNameBlur}
                        validationFunction={validateLastName}
                        hasError={!isValidLastName}
                        errorMessage="Please enter a valid last name."
                        inputId="lastName"
                    />
                </LeadFormInputWrapper>
            </LeadFormRow>
            <LeadFormRow>
                <LeadFormInputWrapper>
                    <Input
                        name="email"
                        value={shopperInformation.email}
                        label="Email"
                        type="text"
                        placeholder="e.g. JSmith@gmail.com"
                        validationFunction={validateEmail}
                        errorMessage="Please enter a valid email address."
                        onChangeFunction={handleChange}
                        hasError={!isValidEmail}
                        onBlurFunction={handleEmailBlur}
                        inputId="email"
                    />
                </LeadFormInputWrapper>
                <LeadFormInputWrapper>
                    <Input
                        name="phone"
                        value={shopperInformation.phone}
                        label={<span>Phone{!isPhoneNumberRequired && <LeadFormSubLabel> (Optional)</LeadFormSubLabel>}</span>}
                        type="text"
                        placeholder="e.g. 456-789-0123"
                        onChangeFunction={handleChange}
                        onBlurFunction={handlePhoneBlur}
                        validationFunction={validateOptionalPhone}
                        hasError={!isValidPhone}
                        errorMessage="Please enter a valid phone number."
                        inputId="phone"
                        data-testid="leadform-phone"
                    />
                </LeadFormInputWrapper>
            </LeadFormRow>
            <LeadFormCommentWrapper>
                <Textarea
                    name="message"
                    maxLength={maxMessageLength}
                    rows={3}
                    value={shopperInformation.message}
                    onChange={handleChange}
                    onBlur={handleMessageBlur}
                    disableResize={true}
                    label={
                        <div>
                            Comments <LeadFormSubLabel>(Optional)</LeadFormSubLabel>
                        </div>
                    }
                />
                <MessageLengthCounter>{`${remainingCharacters} characters remaining`}</MessageLengthCounter>
            </LeadFormCommentWrapper>
            {renderDisclaimer()}
            {renderCCPADisclaimer()}
            <LeadFormButtonWrapper>
                {zipCodeLoading && <SpinnerUI size="30" />}
                <SubmitButton
                    type="button"
                    role="button"
                    data-testid="leadform-submit"
                    disabled={leadFormSubmitting || zipCodeLoading || isTradePaymentFailed}
                    onClick={handleSubmit}
                    status={submitButtonBackground}
                >
                    {submitButtonValue}
                </SubmitButton>
                {renderSkipButton()}
            </LeadFormButtonWrapper>
        </LeadFormContainer>
    );
};

export default LeadFormUI;
